import React, { useEffect, useState, useMemo } from 'react';
import {
  NavItemIndicator,
  ProfilePicture,
  SearchProfileContainer,
  ViewContainer,
} from './NavBar.styles';
import { CustomMenu, CustomMenuItem } from '../../common/StyledComponents';
import { Box } from '@mui/material';
import { useNavigate } from 'react-router';
import featureFlag from '../../common/featureFlag';
import { PermissionKeys } from '../../context/PermissionsContext/permissionTypes';
import { usePermissions } from '../../context/PermissionsContext/PermissionsContext';
import { useTheme } from '../../context/PermissionsContext/ThemeContext/ThemeContext';
import { useQueryClient } from '@tanstack/react-query';
import UserProfilePicture from '../UserProfilePicture';
import { isStarterAccount } from '../../common/utils/isStarterAccount';
import useOrgStore from '../../store/zustand/useOrgStore';
import { Auth } from 'aws-amplify';

interface Props {
  userProfileAvatar?: string;
  activeTab: string;
  anchorEl: HTMLElement | null;
  setAnchorEl: React.Dispatch<React.SetStateAction<HTMLElement | null>>;
  // isProfileDropdownOpen: boolean;
  isMobileMenuOpen: boolean;
  // handleProfileClick: () => void;
  handleMobileMenuToggle: () => void;
  handleSignout: () => void;
  handleNavigate: (path: string) => void;
}

interface NavItem {
  label: string;
  value: string;
}

const NavBarView: React.FC<Props> = ({
  // handleProfileClick,
  activeTab,
  // isProfileDropdownOpen,
  anchorEl,
  setAnchorEl,
  // isMobileMenuOpen,
  handleNavigate,
  // handleMobileMenuToggle,
  handleSignout,
}) => {
  const queryClient = useQueryClient();
  const [isPaivStarter, setIsPaivStarter] = useState(false);
  const [isPaivSuperAdmin, setIsPaivSuperAdmin] = useState(false);

  useEffect(() => {
    setIsPaivStarter(isStarterAccount());
    // @ts-ignore
  }, [(global as any)?.me?.id, (global as any)?.me?.clonedFrom]);

  useEffect(() => {
    const checkPaivSuperAdmin = async () => {
      try {
        const cognitoUser = await Auth.currentAuthenticatedUser();
        const groups =
          cognitoUser.signInUserSession?.accessToken?.payload?.[
          'cognito:groups'
          ] || [];
        setIsPaivSuperAdmin(groups.includes('paiv_admin_rw'));
      } catch {
        setIsPaivSuperAdmin(false);
      }
    };
    checkPaivSuperAdmin();
  }, []);

  const handleSignoutWithCacheInvalidation = () => {
    queryClient.invalidateQueries({ queryKey: ['games'] });
    handleSignout();
  };

  const handleDropdownOpen = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>
  ) => {
    setAnchorEl(e.currentTarget);
  };

  const navigate = useNavigate();
  const { theme } = useTheme();
  const { isSuperAdmin, checkPermission } = usePermissions();
  const isSuper = isSuperAdmin();
  const viewPayments = checkPermission(PermissionKeys.Sales_ViewPayments);
  // @ts-ignore
  // const canAccessSettings = isSuper || global?.me?.isAdmin;

  // @ts-ignore
  // console.log('343434IS SUPER ADMIN', isSuper, global?.me?.id);

  const orgData = useOrgStore((state) => state.orgData);
  const webInterfaceConfig =
    orgData?.configParms?.features?.webInterface ||
    // @ts-ignore - fallback to global for initial load
    global?.appSettings?.features?.webInterface ||
    {};
  const scheduleFeatureEnabled =
    orgData?.configParms?.features?.schedule?.enabled ??
    // @ts-ignore - fallback to global for initial load
    (global as any)?.appSettings?.features?.schedule?.enabled ??
    false;
  const isSchedulingEnabled =
    scheduleFeatureEnabled && !webInterfaceConfig.disableScheduling;
  const schedulingRoute: NavItem | undefined = isSchedulingEnabled
    ? { label: 'Scheduling', value: '/scheduling' }
    : undefined;
  const hideProducts = webInterfaceConfig.hideProducts === true;
  const hideFeed = webInterfaceConfig.hideFeed === true;

  const mapRoute: NavItem | undefined = featureFlag('webMap')
    ? { label: 'Map', value: '/map' }
    : undefined;

  const isAdmin = (global as any)?.me?.isAdmin || isSuper;

  const navItems = useMemo((): NavItem[] => {
    if (isPaivStarter) {
      return [
        { label: 'Pipeline', value: '/pipeline' },
        { label: 'People', value: '/people' },
        ...(isAdmin ? [{ label: 'Settings', value: '/settings' }] : []),
      ];
    }

    const baseItems: NavItem[] = [
      ...(hideFeed ? [] : [{ label: 'Feed', value: '/feed' }]),
      { label: 'Performance', value: '/performance' },
      { label: 'Training', value: '/training' },
      { label: 'People', value: '/people' },
      { label: 'Teams', value: '/teams' },
      { label: 'Pipeline', value: '/pipeline' },
      ...(hideProducts ? [] : [{ label: 'Products', value: '/products' }]),
    ];

    return featureFlag('quicksight')
      ? featureFlag('payments') && viewPayments
        ? [
          ...baseItems,
          { label: 'Dashboard', value: '/dashboard' },
          ...(mapRoute ? [mapRoute] : []),
          { label: 'Payments', value: '/payments' },
          ...(schedulingRoute ? [schedulingRoute] : []),
          ...(isAdmin ? [{ label: 'Settings', value: '/settings' }] : []),
        ]
        : [
          ...baseItems,
          { label: 'Dashboard', value: '/dashboard' },
          ...(mapRoute ? [mapRoute] : []),
          ...(schedulingRoute ? [schedulingRoute] : []),
          ...(isAdmin ? [{ label: 'Settings', value: '/settings' }] : []),
        ]
      : featureFlag('payments') && viewPayments
        ? [
          ...baseItems,
          ...(mapRoute ? [mapRoute] : []),
          { label: 'Payments', value: '/payments' },
          ...(schedulingRoute ? [schedulingRoute] : []),
          ...(isAdmin ? [{ label: 'Settings', value: '/settings' }] : []),
        ]
        : [
          ...baseItems,
          ...(mapRoute ? [mapRoute] : []),
          ...(schedulingRoute ? [schedulingRoute] : []),
          ...(isAdmin ? [{ label: 'Settings', value: '/settings' }] : []),
        ];
  }, [
    isPaivStarter,
    isSuper,
    viewPayments,
    hideProducts,
    hideFeed,
    mapRoute,
    schedulingRoute,
  ]);

  return (
    <ViewContainer
      style={{
        backgroundColor: theme.isDark ? theme.background2 : theme.background1,
      }}
    >
      <div style={{ display: 'flex', gap: 60, alignItems: 'center' }}>
        <Box
          sx={{ cursor: 'pointer' }}
          onClick={() =>
            navigate(isPaivStarter || hideFeed ? '/pipeline' : '/feed')
          }
        >
          <img
            src="/images/paiv-logo-with-text.svg"
            alt="Logo"
            width={120}
            height={120}
          />
        </Box>
        <div style={{ display: 'flex', gap: 20 }}>
          {navItems.filter(Boolean).map((navItem) => {
            return (
              <div
                key={navItem.value}
                onClick={() => handleNavigate(navItem.value)}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                }}
              >
                <div
                  style={{
                    fontWeight: activeTab.includes(navItem.value) ? 600 : 400,
                    cursor: 'pointer',
                    fontSize: 14,
                    color: activeTab.includes(navItem.value)
                      ? theme.primary
                      : theme.text,
                  }}
                >
                  {navItem.label}
                </div>
                <NavItemIndicator
                  isActive={activeTab.includes(navItem.value)}
                />
              </div>
            );
          })}
        </div>
      </div>
      <SearchProfileContainer>
        <ProfilePicture onClick={(e) => handleDropdownOpen(e)}>
          <UserProfilePicture
            // @ts-ignore
            imageName={global?.me?.imageName}
            // @ts-ignore
            initials={global?.me?.name}
            size={40}
          />
          <CustomMenu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={(e: React.MouseEvent<HTMLElement>) => {
              e.stopPropagation();
              setAnchorEl(null);
            }}
          >
            {isPaivSuperAdmin && (
              <CustomMenuItem
                sx={{
                  background: theme.background3,
                  color: theme.text,
                  paddingTop: '9px',
                  paddingBottom: '9px',
                  '&:hover': {
                    backgroundColor: theme.isDark
                      ? theme.primaryDark
                      : theme.primaryLight,
                  },
                }}
                onClick={() => handleNavigate('/admin-config')}
                disableRipple={true}
              >
                Paiv Admin Config
              </CustomMenuItem>
            )}
            {!isPaivStarter && isSuper && (
              <CustomMenuItem
                sx={{
                  background: theme.background1,
                  color: theme.text,
                  paddingTop: '9px',
                  paddingBottom: '9px',
                  '&:hover': {
                    backgroundColor: theme.isDark
                      ? theme.primaryDark
                      : theme.primaryLight,
                  },
                }}
                onClick={() => handleNavigate('/settings')}
                disableRipple={true}
              >
                Settings
              </CustomMenuItem>
            )}
            {!isPaivStarter && (
              <CustomMenuItem
                sx={{
                  background: theme.background1,
                  color: theme.text,
                  paddingTop: '9px',
                  paddingBottom: '9px',
                  '&:hover': {
                    backgroundColor: theme.isDark
                      ? theme.primaryDark
                      : theme.primaryLight,
                  },
                }}
                // @ts-ignore
                onClick={() =>
                  handleNavigate('/people/stats/' + window?.me?.id)
                }
                disableRipple={true}
              >
                Me
              </CustomMenuItem>
            )}

            <CustomMenuItem
              sx={{
                background: theme.background1,
                color: theme.text,
                paddingTop: '9px',
                paddingBottom: '9px',
                '&:hover': {
                  backgroundColor: theme.isDark
                    ? theme.primaryDark
                    : theme.primaryLight,
                },
              }}
              onClick={() => {
                setAnchorEl(null);
                navigate('/v2/pipeline');
              }}
              disableRipple={true}
            >
              Switch to New UI
            </CustomMenuItem>
            <CustomMenuItem
              sx={{
                background: theme.background1,
                color: theme.text,
                paddingTop: '9px',
                paddingBottom: '9px',
                '&:hover': {
                  backgroundColor: theme.isDark
                    ? theme.primaryDark
                    : theme.primaryLight,
                },
              }}
              onClick={handleSignoutWithCacheInvalidation}
              disableRipple={true}
            >
              Sign out
            </CustomMenuItem>
          </CustomMenu>
        </ProfilePicture>
      </SearchProfileContainer>
    </ViewContainer>
  );
};

export default NavBarView;
